import { useState } from "react";
import { EvidenceCard, SubHead } from "./EvidenceCard";
import { DataTable } from "@/components/DataTable";
import { MetricRow } from "@/components/SiteChrome";
import { AcosPill } from "@/components/Pills";
import { fmtMoney, fmtPct, fmtNum } from "@/lib/format";

export const WastedSpendSection = ({ data, cur, badge, footnote }) => (
  <EvidenceCard id="wastedSpend" title="Wasted spend" badge={badge} badgeColor="#007d62" footnote={footnote}>
    <MetricRow
      testId="wasted-spend-metrics"
      items={[
        { label: "Zero-order spend", value: fmtMoney(cur, data.totalNcSpend), color: "#d6453d" },
        { label: "% of total spend", value: fmtPct(data.ncPctOfSpend, 1) },
        { label: "Benchmark", value: `< ${fmtPct(data.benchmarkPct)}`, sub: "of spend non-converting" },
      ]}
    />
    <SubHead>Negative keyword candidates — clicks, no orders</SubHead>
    <DataTable
      testId="negative-candidates-table"
      cap={10}
      totalCount={data.negativeCandidatesTotal}
      rows={data.negativeCandidates || []}
      columns={[
        { key: "term", label: "Search term" },
        { key: "spend", label: "Spend", align: "right", render: (r) => fmtMoney(cur, r.spend) },
        { key: "clicks", label: "Clicks", align: "right", render: (r) => fmtNum(r.clicks) },
        { key: "campaigns", label: "Campaigns", align: "right" },
        { key: "matchTypes", label: "Match types", render: (r) => (r.matchTypes || []).join(", ") },
      ]}
    />
    {(data.duplicateTerms || []).length > 0 && (
      <>
        <SubHead>Duplicate targets — same term bid from multiple campaigns</SubHead>
        <DataTable
          testId="duplicate-terms-table"
          cap={10}
          rows={data.duplicateTerms}
          columns={[
            { key: "term", label: "Search term" },
            { key: "campaignCount", label: "Campaigns", align: "right" },
            { key: "totalSpend", label: "Total spend", align: "right", render: (r) => fmtMoney(cur, r.totalSpend) },
            {
              key: "bestAcos",
              label: "Best vs worst ACoS",
              align: "right",
              render: (r) => (
                <span className="whitespace-nowrap">
                  <AcosPill value={r.bestAcos} /> <span className="text-[color:var(--grey)] mx-1">vs</span> <AcosPill value={r.worstAcos} />
                </span>
              ),
            },
          ]}
        />
      </>
    )}
  </EvidenceCard>
);

export const HarvestSection = ({ data, cur, badge, footnote }) => (
  <EvidenceCard id="harvest" title="Harvest: proven winners" badge={badge} badgeColor="#0d0d0d" footnote={footnote}>
    <p className="text-sm text-[#4A4A4A] mt-1 mb-2 max-w-2xl">
      Terms with 3+ orders still running only via broad, phrase or auto — move them into dedicated exact-match targets.
    </p>
    <SubHead>Harvest candidates</SubHead>
    <DataTable
      testId="harvest-candidates-table"
      cap={10}
      totalCount={data.candidatesTotal}
      rows={data.candidates || []}
      columns={[
        { key: "term", label: "Search term" },
        { key: "sales", label: "Sales", align: "right", render: (r) => fmtMoney(cur, r.sales) },
        { key: "acos", label: "ACoS", align: "right", render: (r) => <AcosPill value={r.acos} /> },
        { key: "orders", label: "Orders", align: "right" },
        { key: "currentMatchTypes", label: "Current match types", render: (r) => (r.currentMatchTypes || []).join(", ") },
      ]}
    />
    {(data.productTargetCandidates || []).length > 0 && (
      <>
        <SubHead>→ Product targets</SubHead>
        <DataTable
          testId="product-target-candidates-table"
          cap={5}
          totalCount={data.productTargetCandidatesTotal}
          rows={data.productTargetCandidates}
          columns={[
            { key: "term", label: "ASIN / target" },
            { key: "sales", label: "Sales", align: "right", render: (r) => fmtMoney(cur, r.sales) },
            { key: "acos", label: "ACoS", align: "right", render: (r) => <AcosPill value={r.acos} /> },
            { key: "orders", label: "Orders", align: "right" },
          ]}
        />
      </>
    )}
  </EvidenceCard>
);

const MT_ROWS = [
  ["exact", "Exact"],
  ["phrase", "Phrase"],
  ["broad", "Broad"],
  ["auto", "Auto"],
  ["product_targets", "Product targets"],
  ["total", "Total"],
];
const MT_TABS = [
  ["totals", "Totals"],
  ["branded", "Branded"],
  ["nonBranded", "Non-branded"],
];

export const MatchTypeSection = ({ data, cur, enablerNote }) => {
  const [tab, setTab] = useState("totals");
  const grid = data[tab] || {};
  const rows = MT_ROWS.filter(([k]) => grid[k]).map(([k, label]) => ({ key: k, label, ...grid[k] }));
  return (
    <EvidenceCard id="matchType" title="Match types" footnote={null}>
      <div className="flex gap-0 border-2 border-[color:var(--ink)] w-fit my-4" data-testid="match-type-tabs">
        {MT_TABS.map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`match-type-tab-${k}`}
            className={`px-4 py-2.5 ss-mono text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${
              tab === k ? "bg-[color:var(--ink)] text-white" : "bg-white text-[color:var(--grey)] hover:text-[color:var(--ink)]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <DataTable
        testId={`match-type-table-${tab}`}
        rows={rows}
        columns={[
          {
            key: "label",
            label: "Match type",
            render: (r) => <span className={r.key === "total" ? "font-bold" : ""}>{r.label}</span>,
          },
          { key: "spend", label: "Spend", align: "right", render: (r) => fmtMoney(cur, r.spend) },
          { key: "sales", label: "Sales", align: "right", render: (r) => fmtMoney(cur, r.sales) },
          { key: "acos", label: "ACoS", align: "right", render: (r) => <AcosPill value={r.acos} /> },
          { key: "ncSpend", label: "Zero-order spend", align: "right", render: (r) => fmtMoney(cur, r.ncSpend) },
          { key: "ncPct", label: "Zero-order %", align: "right", render: (r) => fmtPct(r.ncPct, 1) },
        ]}
      />
      {enablerNote && (
        <p className="mt-6 pt-4 border-t border-[color:var(--hairline)] text-xs text-[color:var(--grey)] leading-relaxed" data-testid="match-type-enabler-note">
          {enablerNote}
        </p>
      )}
    </EvidenceCard>
  );
};
